import React from 'react';

import { ia } from 'lib/helpers/utility';

import ColorDot from 'components/shared/ColorPicker/components/ColorDot';
import { PRACTICE_ROLE_OPTIONS } from 'components/shared/Filters/lib/filterOptions';
import Status from 'components/shared/Status/Status';

import Actions from '../components/Actions';

export const processData = (data) => {
  if (!ia(data)) return data;

  const payers =
    (ia(data[0]?.payer) &&
      data[0]?.payer.map((rowPayer) => {
        const payer = rowPayer.payer;
        return payer ? { value: payer.id, label: payer.name } : null;
      })) ||
    [];

  const procedures =
    (ia(data[0]?.procedure) &&
      data[0].procedure.map((rowProcedure) => {
        const procedure = rowProcedure.procedure;
        return procedure ? { value: procedure.id, label: procedure.name } : null;
      })) ||
    [];

  const users = data.map((row) => {
    const user = row?.user;
    return user ? { value: row.user_id, label: `${row?.user?.f_name} ${row?.user?.l_name}` } : null;
  });

  const newFormatData = { supervisor: data[0]?.supervisor, payers, procedures, users };

  return newFormatData;
};

const keys = ['users', 'procedures', 'payers'];

export const extractIds = (data, key) => {
  if (Array.isArray(data)) {
    return data.map((row) => row.value) || [];
  }

  return keys.includes(key) ? [] : data;
};

export const getChanges = (formik, values) => {
  const changes = { supervisorId: values.supervisorId };

  const objectToSubmit = values;
  const initialValues = formik.initialValues;

  for (const key in objectToSubmit) {
    if (objectToSubmit[key] !== initialValues[key]) {
      changes[key] = extractIds(objectToSubmit[key], key);
    }
  }

  return changes;
};

export const extractNewObjectIds = (data) => {
  const extractedValues = {};
  for (const key in data) {
    extractedValues[key] = extractIds(data[key]);
  }

  return extractedValues;
};

export const getCurrentOption = (options, optionId) => {
  if (!optionId) return null;
  if (!ia(options, 0)) return null;
  return options?.find((role) => role.value === optionId);
};

const STAFF_STATUS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' }
];

export const defaultFilters = {
  search: {
    type: 'search',
    fields: [
      { field: 'id', label: 'ID' },
      { field: 'name', label: 'Name' },
      { field: 'email', label: 'Email' }
    ]
  },
  name: {
    title: 'Name',
    placeholder: 'Search name',
    allowed_operators: ['contains', 'does not contain'],
    icon: 'first-line-mono'
  },
  email: {
    title: 'Email',
    placeholder: 'Search email',
    allowed_operators: ['contains', 'does not contain'],
    icon: 'mail-mono'
  },
  roles: PRACTICE_ROLE_OPTIONS,
  status: {
    title: 'Status',
    queryFn: (_, data) => {
      const searchTerm = data.filters.searchTerm || '';
      return {
        options: STAFF_STATUS.filter((status) =>
          status.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      };
    },
    queryKey: 'staff_statuses.options',
    icon: 'status-mono'
  },
  color: {
    title: 'Color',
    allowed_operators: ['contains', 'does not contain'],
    icon: 'paint-bucket-mono'
  }
};

export const defaultColumns = () => [
  {
    field: 'name',
    headerName: 'First name',
    cellClass: 'no-border',
    flex: 1,
    valueGetter: ({ data }) => data?.f_name
  },
  {
    field: 'l_name',
    headerName: 'Last name',
    cellClass: 'no-border',
    flex: 1,
    valueGetter: ({ data }) => data?.l_name
  },
  {
    field: 'role',
    headerName: 'Role',
    cellClass: 'no-border',
    flex: 1
  },
  {
    field: 'email',
    headerName: 'Email',
    cellClass: 'no-border',
    flex: 1
  },
  {
    field: 'status',
    headerName: 'Status',
    cellClass: 'no-border',
    flex: 1,
    cellRenderer: ({ value }) => <Status status={value} />
  },
  {
    field: 'color',
    headerName: 'Color',
    cellClass: 'no-border',
    flex: 1,
    cellRenderer: ({ value }) => {
      return (
        <div className="flex h-full items-center justify-start">
          <ColorDot selectedColor={value} />
        </div>
      );
    }
  },
  {
    field: 'actions',
    headerName: 'Actions',
    cellClass: 'no-border',
    cellRenderer: Actions,
    maxWidth: 88,
    minWidth: 36,
    resizable: false,
    sortable: false,
    pinned: 'right'
  }
];
